import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Sparkle, Warning, ArrowRight } from "@phosphor-icons/react";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 15; // ~30s

const TrialSuccess = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState("loading"); // loading | active | pending | error
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setErrorMsg("Missing checkout session ID.");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await axios.get(`${API}/trial/status/${sessionId}`);
        if (res.data.trial_active) {
          setTrialEndsAt(res.data.trial_ends_at);
          setState("active");
          if (refreshUser) refreshUser();
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        setErrorMsg(e.response?.data?.detail || "Could not verify your trial. Please try again.");
        setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="trial-success-page">
      <Card className="bg-zinc-950 border-zinc-800 max-w-xl w-full">
        <CardContent className="p-10 text-center space-y-5">
          {state === "loading" && (
            <>
              <div className="loading-spinner mx-auto w-10 h-10 border-emerald-500/40" />
              <h2 className="text-xl font-bold">Verifying your trial…</h2>
              <p className="text-sm text-zinc-400">Confirming your card was saved securely with Stripe.</p>
            </>
          )}

          {state === "active" && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10">
                <CheckCircle weight="fill" className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold">Your Premium Trial is Active</h2>
              <p className="text-sm text-zinc-400">
                You have full Premium access for the next 14 days.
                {trialEndsAt && (
                  <> Trial ends <strong className="text-white">{new Date(trialEndsAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</strong>.</>
                )}
              </p>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4 text-left space-y-2 text-sm text-zinc-300">
                <p className="flex items-start gap-2"><Sparkle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> 3 Flash Drafts included during trial</p>
                <p className="flex items-start gap-2"><Sparkle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> Corporate Scout, Cross-Collaboration, Impact Dashboard — all unlocked</p>
                <p className="flex items-start gap-2"><Sparkle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> Your card is saved but <strong>will NOT be charged automatically</strong></p>
              </div>
              <Button
                onClick={() => navigate("/grants")}
                className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm cta-hover"
                data-testid="continue-btn"
              >
                Explore Grants <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {state === "pending" && (
            <>
              <Warning className="w-12 h-12 text-amber-400 mx-auto" />
              <h2 className="text-xl font-bold">Processing your trial…</h2>
              <p className="text-sm text-zinc-400">
                Stripe is taking a bit longer than usual. Check back in a minute — your account will be updated automatically.
              </p>
              <Button
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="border-zinc-700 rounded-sm"
                data-testid="back-to-pricing-btn"
              >
                Back to Pricing
              </Button>
            </>
          )}

          {state === "error" && (
            <>
              <Warning className="w-12 h-12 text-red-400 mx-auto" />
              <h2 className="text-xl font-bold">Something went wrong</h2>
              <p className="text-sm text-zinc-400">{errorMsg}</p>
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                data-testid="retry-btn"
              >
                Back to Pricing
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrialSuccess;
